import * as React from "react";
import styled from "@emotion/styled";
import { H2, Link } from "atoms";
import { OpenCallCard } from "./OpenCallCard";
import { OpenCallCardGrid } from "./OpenCallCardGrid";
import { useOpenCalls } from "../api/useOpenCalls";
import { categorizeOpenCalls } from "../utils/categorizeOpenCalls";
import type { OpenCallTag } from "../types";

type OpenCallViewMoreProps = {
  currentOpenCallId?: string;
  tag: Exclude<OpenCallTag, "Voting Open">;
  headline?: string;
};

function OpenCallViewMore({
  currentOpenCallId = "",
  tag,
  headline = "Explore More Open Calls",
}: OpenCallViewMoreProps) {
  const { data: openCalls } = useOpenCalls({ isExclusive: false });

  const openCallsOfTag = React.useMemo(
    () => categorizeOpenCalls(openCalls?.data ?? [], currentOpenCallId)[tag],
    [openCalls, currentOpenCallId, tag],
  );

  if (!openCallsOfTag.length) {
    return null;
  }

  return (
    <SectionWithHeading>
      <H2 size="h3">{headline}</H2>

      <OpenCallCardGrid>
        {openCallsOfTag.map((openCall) => (
          <OpenCallCard key={openCall.id} openCall={openCall} />
        ))}
      </OpenCallCardGrid>

      <Link to="/open-calls" variant="primary" size="xs">
        View All Open Calls
      </Link>
    </SectionWithHeading>
  );
}

const SectionWithHeading = styled.div(({ theme }) => ({
  display: "grid",
  gap: "2rem 1rem",
  gridTemplateAreas: '"title" "content" "button"',
  margin: "2rem 0",

  [theme.breakpoints.desktop]: {
    gridTemplateAreas: '"title button" "content content"',
    gridTemplateColumns: "1fr auto",
    alignItems: "end",
  },
  "&>": {
    "h2,h3,h4": {
      gridArea: "title",
      margin: 0,
    },
    div: {
      gridArea: "content",

      [theme.breakpoints.desktop]: {
        "&>div:last-of-type": {
          display: "none",
        },
      },
      [theme.breakpoints.desktopLarge]: {
        "&>div:last-of-type": {
          display: "flex",
        },
      },
    },
    a: {
      gridArea: "button",
      width: "100%",
      maxWidth: "max-content",
    },
  },
}));

export { OpenCallViewMore };
